<template>
	<TransitionRoot :show="isOpen" appear as="template">
		<Dialog as="div" @close="close">
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="duration-300 ease-out"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div class="fixed inset-0 z-50 bg-gray-500 bg-opacity-60" />
			</TransitionChild>
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="translate-x-full"
				enter-to="translate-x-0"
				leave="duration-300 ease-out"
				leave-from="translate-x-0"
				leave-to="translate-x-full"
			>
				<DialogPanel
					class="w-full shadow-xl flex flex-col fixed inset-y-0 right-0 z-50 overflow-y-auto bg-white sm:max-w-[448px]"
				>
					<div class="w-full pointer-events-auto h-full">
						<div
							class="flex h-full w-full flex-col bg-white shadow-xl py-6 px-4 sm:p-6"
						>
							<div class="flex flex-col h-full relative">
								<div
									v-if="cartStore.updating"
									class="absolute inset-0 z-50 bg-gray-100 bg-opacity-60 flex items-center justify-center"
								>
									<div
										class="inline-block h-5 w-5 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
										role="status"
									></div>
								</div>

								<div class="flex items-start justify-between">
									<h2 id="slide-over-title" class="text-lg font-medium py-0">
										{{ $t("Shopping bag") }} ({{ itemsCount }})
									</h2>
									<div class="ml-3 flex h-7 items-center">
										<button
											type="button"
											class="-m-2 p-2 text-primary !outline-none"
											@click="isOpen = false"
										>
											<span class="sr-only">Close panel</span>
											<XMarkIcon class="w-6 h-6" />
										</button>
									</div>
								</div>

								<div
									v-if="isEmpty"
									class="flex-1 min-h-0 text-center flex flex-col justify-center"
								>
									<h4 class="mb-2 font-medium text-2xl text-empty-cart-header">
										{{ $t("Your shopping bag is empty") }}
									</h4>
									<p
										class="mb-6 text-base text-gray-super-dark text-empty-cart-desc"
									>
										{{
											$t(
												"Looks like you haven’t added any items to the cart yet. Start shopping to fill it in."
											)
										}}
									</p>
									<nuxt-link
										:to="localePath('/')"
										class="bg-graycustom px-6 py-3 text-base font-medium text-primary"
										@click="close"
										>{{ $t("Start Shopping") }}
									</nuxt-link>
								</div>

								<div
									v-else
									class="flex flex-col flex-1 min-h-0 gap-4 sm:gap-6 mt-6"
								>
									<div class="flex-1 min-h-0 overflow-y-auto -mr-6">
										<SharedProductOrders
											class="flex-1 -mt-6 pr-6"
											:is-mini-cart="true"
											:line-items="cartItems || []"
										/>
										<ProductRecommendationList
											v-if="cartRecommendations"
											class="pr-6 pt-8"
											:products="recommendedProducts"
											:title="cartRecommendations.title"
											:show-add-to-cart="cartRecommendations.show_add_to_cart"
											title-styles="text-base font-bold"
											grid-class="grid grid-cols-2 gap-4 w-full"
											@product-click="trackRecommendationClick"
										/>
									</div>
									<div class="flex flex-col mt-auto">
										<div class="flex flex-col gap-2">
											<div
												v-if="orderTotal?.AmountIncVat"
												class="flex justify-between text-lg font-medium"
											>
												<p>{{ $t("Order total (inc. shipping)") }}</p>
												<Price :price="orderTotal.AmountIncVat" />
											</div>
											<div
												v-if="orderTotal?.Vat"
												class="flex justify-between text-sm text-gray-super-dark font-medium"
											>
												<p>{{ $t("Whereof VAT") }}</p>
												<Price :price="orderTotal.Vat" />
											</div>
										</div>
										<div
											v-if="shippingItem"
											class="mt-5 flex items-center gap-2 text-sm text-gray-500"
										>
											<InformationCircleIcon class="h-6 w-6" />
											{{
												$t(
													"Total price includes the '{method}' default shipping method at {cost}.",
													{
														method: shippingItem.Name,
														cost: formatPrice(shippingItem.PriceDisplayIncVat)
													}
												)
											}}
										</div>
										<div class="flex flex-col">
											<nuxt-link
												class="mt-3 flex items-center justify-center px-5 py-3 text-base font-medium text-white shadow-sm bg-main-button"
												:to="localePath('/checkout')"
												@click="close"
											>
												{{ $t("Go to checkout") }}
											</nuxt-link>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</DialogPanel>
			</TransitionChild>
		</Dialog>
	</TransitionRoot>
</template>

<script setup lang="ts">
import { InformationCircleIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import {
	Dialog,
	DialogPanel,
	TransitionRoot,
	TransitionChild
} from "@headlessui/vue";
import { computed } from "vue";
import Price from "../Product/ProductAttributes/Price.vue";
import { isShippingItem } from "~/utils/cart/isShippingItem";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";

const { helloRetailTrack } = useHelloRetailTracking();

const { viewCart } = useGtmEvents();

const isOpen = inject<Ref<boolean>>("isSidebarOpen", ref(false));

const cartStore = useCartStore();
const { formatPrice } = useFormatPrice({ groupThousands: true });

const { basket } = storeToRefs(cartStore);

const filterItems = (items?: BasketItem[]) => {
	return items?.filter((item) => !isShippingItem(item));
};

const cartItems = computed(() => filterItems(basket?.value?.Items));
const itemsCount = computed(() => cartStore?.itemsCount());
const orderTotal = computed(() => basket?.value?.Summary?.Total);
const isEmpty = computed(() => !basket?.value?.Items?.length);
const shippingItem = computed(() => {
	return basket?.value?.Items?.find((item) => isShippingItem(item));
});

const localePath = useLocalePath();

const close = () => {
	isOpen.value = false;
};

// Configured on `layouts/main` so the panel has it whichever page it opens
// from. No blok = nothing renders. Fetched only once the panel opens.
const { cartRecommendations } = storeToRefs(useAppStore());
const {
	products: recommendedProducts,
	trackProductClick: trackRecommendationClick
} = useProductRecommendations({
	scope: "cart",
	productBoxKey: () => cartRecommendations.value?.product_box_key,
	// The cart recommends against everything in the basket, not one product —
	// which is also why no Meilisearch fallback is passed: there is no single
	// brand or category to fall back on.
	context: computed(() => ({
		productNumbers: (cartItems.value || [])
			.map((item) => item.PartNo)
			.filter((partNo): partNo is string => !!partNo)
	})),
	limit: () => cartRecommendations.value?.products_limit ?? 4,
	enabled: isOpen
});

// Opening the mini cart is the GA4 view_cart step. This fork predates the
// base wiring, so without it the funnel jumped from add_to_cart straight to
// begin_checkout.
watch(isOpen, () => {
	if (isOpen.value) {
		viewCart(cartItems.value || [], orderTotal.value?.AmountIncVat || 0);
	}
});

watch(orderTotal, async (newTotal, oldTotal) => {
	if (
		oldTotal === undefined ||
		newTotal?.AmountIncVat !== oldTotal?.AmountIncVat
	) {
		const email = userStore().getUser?.Email || undefined;
		helloRetailTrack({
			serviceType: "cart",
			trackingData: {
				total: orderTotal.value?.AmountIncVat || 0,
				productNumbers: cartItems?.value?.map((item) => item.PartNo) || [],
				email: email
			}
		});
	}
});
</script>
